import { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, RefreshCw, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { diagnosticsAPI, pennylaneBankFeedAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Static sentinel for the internal-account <select>: kept as a hand-written
// option (not part of the .map() below) so it is reliably selectable via
// fireEvent in this environment's test harness, which cannot drive
// dynamically-rendered <option> elements inside a native <select> (see the
// scope note in this component's test file).
const CREATE_NEW_VALUE = '__create_new__';

// Best-effort institution guess from the Pennylane account's own display
// name, purely a prefill convenience -- always editable, and the backend
// requires a non-blank bank_name regardless of whether this guessed right.
const INSTITUTION_GUESSES = [
  [/\bbcp\b/i, 'Banque BCP'],
  [/\bbnp\b/i, 'BNP Paribas'],
  [/soci.t.\s*g.n.rale|socgen/i, 'Société Générale'],
  [/cr.dit agricole/i, 'Crédit Agricole'],
  [/\blcl\b/i, 'LCL'],
  [/boursorama/i, 'Boursorama'],
  [/\bhsbc\b/i, 'HSBC'],
  [/\bbarclays\b/i, 'Barclays'],
  [/\blloyds\b/i, 'Lloyds'],
  [/\bnatwest\b/i, 'NatWest'],
  [/\brevolut\b/i, 'Revolut'],
  [/\bwise\b/i, 'Wise'],
  [/\bmonzo\b/i, 'Monzo'],
  [/\bstarling\b/i, 'Starling'],
];

export function guessInstitution(pennylaneAccountName) {
  if (!pennylaneAccountName) return '';
  const hit = INSTITUTION_GUESSES.find(([re]) => re.test(pennylaneAccountName));
  return hit ? hit[1] : '';
}

const BLANK_NEW_ACCOUNT = { accountName: '', bankName: '', currency: 'EUR', iban: '' };

/**
 * Creates a Pennylane bank-feed connection mapping: a Pennylane bank
 * account (entity + pennylane bank_account id) -> an internal BR OS bank
 * account. The one-active-feed-per-internal-account guard is enforced
 * server-side at submit time; this UI surfaces the account's existing feed
 * source up front so the operator sees the conflict before submitting.
 *
 * The internal account can also be created on the spot ("+ Create new
 * internal account") when no existing account matches the Pennylane
 * account being mapped -- account creation and mapping creation are one
 * submit, backend-atomic (a rollback deletes the account if the mapping
 * fails validation).
 */
export function PennylaneBankFeedConfigureModal({ open, onOpenChange, onSaved }) {
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [loadingEntities, setLoadingEntities] = useState(false);

  const [pennylaneAccounts, setPennylaneAccounts] = useState([]);
  const [loadingPennylaneAccounts, setLoadingPennylaneAccounts] = useState(false);
  const [pennylaneAccountId, setPennylaneAccountId] = useState('');

  const [internalAccounts, setInternalAccounts] = useState([]);
  const [loadingInternalAccounts, setLoadingInternalAccounts] = useState(false);
  const [internalAccountId, setInternalAccountId] = useState('');
  const [newAccount, setNewAccount] = useState(BLANK_NEW_ACCOUNT);

  const [saving, setSaving] = useState(false);

  const loadEntities = useCallback(async () => {
    setLoadingEntities(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneEntities();
      setEntities(data.entities || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingEntities(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadEntities();
      setEntityId('');
      setPennylaneAccounts([]);
      setPennylaneAccountId('');
      setInternalAccounts([]);
      setInternalAccountId('');
      setNewAccount(BLANK_NEW_ACCOUNT);
    }
  }, [open, loadEntities]);

  const loadPennylaneAccounts = useCallback(async (selectedEntityId) => {
    setLoadingPennylaneAccounts(true);
    setPennylaneAccounts([]);
    setPennylaneAccountId('');
    try {
      const { data } = await diagnosticsAPI.pennylaneBankFeedDiscovery(selectedEntityId, null);
      setPennylaneAccounts(data.accounts || []);
      if (data.error) {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPennylaneAccounts(false);
    }
  }, []);

  const loadInternalAccounts = useCallback(async (selectedEntityId) => {
    setLoadingInternalAccounts(true);
    setInternalAccounts([]);
    setInternalAccountId('');
    try {
      const { data } = await pennylaneBankFeedAPI.listInternalAccounts(selectedEntityId);
      setInternalAccounts(data.accounts || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingInternalAccounts(false);
    }
  }, []);

  const onEntityChange = (value) => {
    setEntityId(value);
    setNewAccount(BLANK_NEW_ACCOUNT);
    if (value) {
      loadPennylaneAccounts(value);
      loadInternalAccounts(value);
    }
  };

  const selectedInternalAccount = internalAccounts.find((a) => a.id === internalAccountId);
  const selectedEntity = entities.find((e) => e.entity_id === entityId);
  const isCreatingNew = internalAccountId === CREATE_NEW_VALUE;

  const onInternalAccountChange = (value) => {
    setInternalAccountId(value);
    if (value === CREATE_NEW_VALUE) {
      const pennylaneAccount = pennylaneAccounts.find(
        (a) => String(a.pennylane_account_id) === String(pennylaneAccountId),
      );
      setNewAccount({
        accountName: pennylaneAccount?.name || '',
        bankName: guessInstitution(pennylaneAccount?.name),
        currency: pennylaneAccount?.currency || 'EUR',
        iban: '',
      });
    }
  };

  const newAccountValid = newAccount.accountName.trim() && newAccount.bankName.trim();

  const save = async () => {
    if (!entityId || !pennylaneAccountId || !internalAccountId) {
      toast.error('Select an entity, a Pennylane account, and an internal account.');
      return;
    }
    if (isCreatingNew && !newAccountValid) {
      toast.error('Name and institution are required to create a new internal account.');
      return;
    }
    setSaving(true);
    try {
      const pennylaneAccount = pennylaneAccounts.find(
        (a) => String(a.pennylane_account_id) === String(pennylaneAccountId),
      );
      const body = {
        entity_id: entityId,
        pennylane_account_id: String(pennylaneAccountId),
        pennylane_account_name: pennylaneAccount?.name || null,
      };
      if (isCreatingNew) {
        body.new_internal_account = {
          account_name: newAccount.accountName.trim(),
          bank_name: newAccount.bankName.trim(),
          currency: newAccount.currency || 'EUR',
          iban: newAccount.iban || '',
        };
      } else {
        body.internal_bank_account_id = internalAccountId;
      }
      await pennylaneBankFeedAPI.createMapping(body);
      toast.success('Mapping created.');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Configure Pennylane bank feed mapping</DialogTitle>
          <DialogDescription>
            Maps a Pennylane bank account to an internal BR OS bank account for
            daily transaction sync. One active feed per internal account is
            enforced; an account already fed by another provider is blocked here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium block mb-1">Entity</label>
            <select
              data-testid="pl-bankfeed-configure-entity"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
              value={entityId}
              onChange={(e) => onEntityChange(e.target.value)}
              disabled={loadingEntities}
            >
              <option value="">Select an entity...</option>
              {entities.map((e) => (
                <option key={e.entity_id} value={e.entity_id}>{e.canonical_name}</option>
              ))}
            </select>
          </div>

          {entityId && (
            <div>
              <label className="text-xs font-medium block mb-1">Pennylane bank account</label>
              {loadingPennylaneAccounts ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Discovering accounts...
                </div>
              ) : (
                <select
                  data-testid="pl-bankfeed-configure-pennylane-account"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                  value={pennylaneAccountId}
                  onChange={(e) => setPennylaneAccountId(e.target.value)}
                >
                  <option value="">Select a Pennylane account...</option>
                  {pennylaneAccounts.map((a) => (
                    <option key={a.pennylane_account_id} value={a.pennylane_account_id}>
                      {a.name || '(unnamed)'} ({a.iban_masked || 'no IBAN'}) id={a.pennylane_account_id}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {entityId && (
            <div>
              <label className="text-xs font-medium block mb-1">Internal BR OS bank account</label>
              {loadingInternalAccounts ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading internal accounts...
                </div>
              ) : (
                <select
                  data-testid="pl-bankfeed-configure-internal-account"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                  value={internalAccountId}
                  onChange={(e) => onInternalAccountChange(e.target.value)}
                >
                  <option value="">Select an internal account...</option>
                  <option value={CREATE_NEW_VALUE}>+ Create new internal account</option>
                  {internalAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name || a.bank_name || a.id} ({a.currency})
                      {a.has_live_feed ? ` -- already fed by ${a.feed_source}` : ''}
                    </option>
                  ))}
                </select>
              )}
              {selectedInternalAccount?.has_live_feed && (
                <p
                  className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1.5"
                  data-testid="pl-bankfeed-configure-conflict-warning"
                >
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  This account already has an active &apos;{selectedInternalAccount.feed_source}&apos; feed.
                  Saving will be blocked until it is deactivated.
                </p>
              )}
              {isCreatingNew && (
                <div
                  className="mt-2 space-y-2 rounded-md border border-input p-3"
                  data-testid="pl-bankfeed-configure-new-account-block"
                >
                  <div>
                    <label className="text-xs font-medium block mb-1">Name</label>
                    <input
                      data-testid="pl-bankfeed-configure-new-account-name"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                      value={newAccount.accountName}
                      onChange={(e) => setNewAccount((s) => ({ ...s, accountName: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1">Institution (required)</label>
                    <input
                      data-testid="pl-bankfeed-configure-new-account-institution"
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                      value={newAccount.bankName}
                      onChange={(e) => setNewAccount((s) => ({ ...s, bankName: e.target.value }))}
                      placeholder="e.g. Banque BCP"
                    />
                    {!newAccount.bankName.trim() && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Required -- an account with no institution renders under
                        &quot;Unknown&quot; on the per-bank chart.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium block mb-1">Currency</label>
                      <input
                        data-testid="pl-bankfeed-configure-new-account-currency"
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                        value={newAccount.currency}
                        onChange={(e) => setNewAccount((s) => ({ ...s, currency: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">IBAN (optional)</label>
                      <input
                        data-testid="pl-bankfeed-configure-new-account-iban"
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                        value={newAccount.iban}
                        onChange={(e) => setNewAccount((s) => ({ ...s, iban: e.target.value }))}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground" data-testid="pl-bankfeed-configure-new-account-jurisdiction">
                    Jurisdiction: {selectedEntity?.jurisdiction || 'unknown'} (derived from entity, not editable here)
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-2">
          <Button variant="ghost" size="sm" onClick={loadEntities} disabled={loadingEntities}>
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
            Refresh entities
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={
              saving || !entityId || !pennylaneAccountId || !internalAccountId ||
              (isCreatingNew && !newAccountValid)
            }
            data-testid="pl-bankfeed-configure-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
            Save mapping
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
